import type {
  Word,
  BuscarScene,
  Cuento,
  JwHistoria,
  JwComic,
  JwVideo,
  LetterSounds,
  Config,
  CategoryConfig,
  Personaje,
  Lugar,
  Oracion,
  IllustrationStyle,
  MascotasData,
  PiezasData,
  EscenaMentalItem,
} from './types';
import type { HeardWord } from './lib/alignWords';

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json() as Promise<T>;
}

export function getAdminKey(): string {
  return sessionStorage.getItem('adminKey') ?? '';
}

export function setAdminKey(key: string): void {
  sessionStorage.setItem('adminKey', key);
  if (typeof document !== 'undefined') {
    if (key) {
      document.cookie = `clarisa_auth=${encodeURIComponent(key)}; max-age=86400; path=/; SameSite=Strict`;
    } else {
      document.cookie = 'clarisa_auth=; max-age=0; path=/; SameSite=Strict';
    }
  }
}

export function adminHeaders(): HeadersInit {
  return { 'X-Admin-Key': getAdminKey(), 'Content-Type': 'application/json' };
}

async function adminPost<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json() as Promise<T>;
}

/* ── Guarda contra el borrado masivo ──────────────────────────────────────
   Los save* de listas reemplazan todo: lo que no viene, el servidor lo borra.
   Si una carga falló, el admin veía la lista vacía, y al agregar un elemento
   mandaba solo ese y se borraba el resto.

   Cada get de una lista anota cuántos elementos devolvió, y el save manda ese
   número en `X-Expected-Count`. El servidor (functions/_lib/saveGuard.js)
   rechaza con 409 una lista más corta que la guardada si el número no
   coincide: la carga no se hizo, o la lista cambió en otro dispositivo. */
type ListKind =
  | 'words' | 'buscar' | 'cuentos' | 'jwHistorias' | 'jwComics' | 'jwVideos'
  | 'categories' | 'personajes' | 'lugares' | 'oraciones' | 'mascotas'
  | 'estilos' | 'escenasMentales';

const loadedCount = new Map<ListKind, number>();

function tracked<T>(kind: ListKind, url: string, count: (data: T) => number | undefined): Promise<T> {
  return get<T>(url).then((data) => {
    const n = count(data);
    if (typeof n === 'number') loadedCount.set(kind, n);
    return data;
  });
}

async function replaceAll<T>(kind: ListKind, url: string, body: unknown, sent: number): Promise<T> {
  const known = loadedCount.get(kind);
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...adminHeaders(), 'X-Expected-Count': known === undefined ? 'none' : String(known) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = res.status === 409
      ? ((await res.json().catch(() => null)) as { error?: string } | null)?.error
      : undefined;
    // Muchas pantallas del admin solo hacen haptic() al fallar, que en
    // escritorio y iPad no se nota. Esto no puede pasar en silencio.
    if (detail) window.alert(detail);
    throw new Error(detail ?? `${url} → ${res.status}`);
  }
  const json = (await res.json()) as T & { count?: unknown };
  // El servidor puede guardar menos de lo mandado (descarta vacíos): manda `count`.
  loadedCount.set(kind, typeof json.count === 'number' ? json.count : sent);
  return json;
}

export async function uploadFile(data: string, filename: string, folder: string): Promise<{ path: string }> {
  return adminPost('/api/uploadImage', { data, filename, folder });
}

/**
 * Video files are too large for the base64-in-JSON `uploadImage` path (33%
 * overhead on top of an already-large binary, plus Cloudflare's ~100MB
 * request body ceiling on Free/Pro). This streams the raw file as the
 * request body instead, and reports upload progress via XHR (fetch has no
 * upload-progress event) since a multi-minute upload with zero feedback
 * reads as a frozen screen.
 */
export function uploadVideoFile(
  file: Blob,
  filename: string,
  onProgress?: (pct: number) => void
): Promise<{ path: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/uploadVideo?filename=${encodeURIComponent(filename)}`);
    xhr.setRequestHeader('X-Admin-Key', getAdminKey());
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('uploadVideo → respuesta inválida')); }
      } else {
        reject(new Error(`uploadVideo → ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('uploadVideo → error de red'));
    xhr.send(file);
  });
}

export type ItunesTrack = {
  trackId: number;
  trackName: string;
  artistName: string;
  artworkUrl100: string;
  previewUrl: string;
};

export async function itunesSearch(query: string): Promise<ItunesTrack[]> {
  const cleanQ = query.trim();
  if (!cleanQ) return [];

  // 1. Intentar primero a través del proxy /api/itunesSearch
  try {
    const url = `/api/itunesSearch?q=${encodeURIComponent(cleanQ)}`;
    const res = await fetch(url, { headers: { 'X-Admin-Key': getAdminKey() } });
    if (res.ok) {
      const data = await res.json() as { results?: ItunesTrack[]; error?: string };
      if (!data.error && Array.isArray(data.results)) {
        return data.results;
      }
    }
  } catch {
    // Si el proxy falla, cae al fallback directo
  }

  // 2. Fallback directo a iTunes Search API (soporta CORS nativo Access-Control-Allow-Origin: *)
  // Evita fallos 502 por bloqueos de IP en Cloudflare o proxy local caído
  try {
    const directUrl = `https://itunes.apple.com/search?term=${encodeURIComponent(cleanQ)}&media=music&limit=15&country=AR`;
    let res = await fetch(directUrl, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) {
      const globalUrl = `https://itunes.apple.com/search?term=${encodeURIComponent(cleanQ)}&media=music&limit=15`;
      res = await fetch(globalUrl, { headers: { 'Accept': 'application/json' } });
    }
    if (!res.ok) throw new Error(`itunesSearch → ${res.status}`);
    const data = await res.json() as { results?: Array<{ trackId: number; trackName: string; artistName: string; artworkUrl100: string; previewUrl?: string }> };
    return (data.results ?? [])
      .filter((r) => r.previewUrl)
      .map((r) => ({
        trackId: r.trackId,
        trackName: r.trackName,
        artistName: r.artistName,
        artworkUrl100: r.artworkUrl100,
        previewUrl: r.previewUrl!,
      }));
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : 'Error al buscar en iTunes');
  }
}

export async function itunesFetch(
  url: string,
  filename: string,
  folder: 'sounds' | 'images'
): Promise<{ path: string }> {
  const res = await fetch('/api/itunesFetch', {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify({ url, filename, folder }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`itunesFetch → ${res.status} ${t}`);
  }
  return res.json() as Promise<{ path: string }>;
}

export type GetConfigResponse = {
  config?: Partial<Config>;
  categories?: CategoryConfig[];
};

export const api = {
  getData: () => tracked<Word[]>('words', '/api/getData', (d) => d.length),
  getBuscar: () => tracked<BuscarScene[]>('buscar', '/api/getBuscar', (d) => d.length),
  getCuentos: () => tracked<Cuento[]>('cuentos', '/api/getCuentos', (d) => d.length),
  getJwHistorias: () => tracked<JwHistoria[]>('jwHistorias', '/api/getJwHistorias', (d) => d.length),
  getJwComics: () => tracked<JwComic[]>('jwComics', '/api/getJwComics', (d) => d.length),
  getJwVideos: () => tracked<JwVideo[]>('jwVideos', '/api/getJwVideos', (d) => d.length),
  getLetterSounds: () => get<LetterSounds>('/api/getLetterSounds'),
  getConfig: () => tracked<GetConfigResponse>('categories', '/api/getConfig', (d) => d.categories?.length),
  checkAuth: (key: string) =>
    fetch('/api/checkAuth', { headers: { 'X-Admin-Key': key } })
      .then((r) => r.ok),
  saveData: (words: Word[]) => replaceAll<{ ok: boolean }>('words', '/api/saveData', words, words.length),
  saveBuscar: (scenes: BuscarScene[]) => replaceAll<{ ok: boolean }>('buscar', '/api/saveBuscar', scenes, scenes.length),
  saveCuentos: (cuentos: Cuento[]) => replaceAll<{ ok: boolean }>('cuentos', '/api/saveCuentos', cuentos, cuentos.length),
  saveJwHistorias: (items: JwHistoria[]) => replaceAll<{ ok: boolean }>('jwHistorias', '/api/saveJwHistorias', items, items.length),
  saveJwComics: (items: JwComic[]) => replaceAll<{ ok: boolean }>('jwComics', '/api/saveJwComics', items, items.length),
  saveJwVideos: (items: JwVideo[]) => replaceAll<{ ok: boolean }>('jwVideos', '/api/saveJwVideos', items, items.length),
  saveConfig: (payload: { categories: CategoryConfig[]; config: Partial<Config> }) =>
    replaceAll<{ ok: boolean }>('categories', '/api/saveConfig', payload, payload.categories.length),
  /** Solo la grabación de una palabra (ver functions/api/saveWordSound.js). */
  saveWordSound: (word: string, lang: 'es' | 'en', path: string) =>
    adminPost<{ success: boolean }>('/api/saveWordSound', { word, lang, path }),
  saveLetterSound: (key: string, path: string) =>
    adminPost<{ ok: boolean }>('/api/saveLetterSound', { key, path }),
  getPersonajes: () => tracked<Personaje[]>('personajes', '/api/getPersonajes', (d) => d.length),
  savePersonajes: (items: Personaje[]) =>
    replaceAll<{ ok?: boolean; success?: boolean }>('personajes', '/api/savePersonajes', items, items.length),
  getLugares: () => tracked<Lugar[]>('lugares', '/api/getLugares', (d) => d.length),
  saveLugares: (items: Lugar[]) =>
    replaceAll<{ ok?: boolean; success?: boolean }>('lugares', '/api/saveLugares', items, items.length),
  getAlignments: () => get<Record<string, HeardWord[]>>('/api/getAlignments'),
  alignAudio: (audio: string, text?: string) =>
    adminPost<{ success: boolean; words: HeardWord[] }>('/api/alignAudio', { audio, text }),
  getOraciones: () => tracked<Oracion[]>('oraciones', '/api/getOraciones', (d) => d.length),
  saveOraciones: (items: Oracion[]) =>
    replaceAll<{ success: boolean }>('oraciones', '/api/saveOraciones', items, items.length),
  getMascotas: () => tracked<MascotasData>('mascotas', '/api/getMascotas', (d) => d.mascotas?.length),
  saveMascotas: (data: MascotasData) =>
    replaceAll<{ success: boolean }>('mascotas', '/api/saveMascotas', data, data.mascotas.length),
  getPiezas: () => get<PiezasData>('/api/getPiezas'),
  savePiezas: (data: PiezasData) => adminPost<{ success: boolean }>('/api/savePiezas', data),
  getEstilos: () => tracked<IllustrationStyle[]>('estilos', '/api/getEstilos', (d) => d.length),
  saveEstilos: (items: IllustrationStyle[]) =>
    replaceAll<{ ok?: boolean; success?: boolean }>('estilos', '/api/saveEstilos', items, items.length),
  getEscenasMentales: () => tracked<EscenaMentalItem[]>('escenasMentales', '/api/getEscenasMentales', (d) => d.length),
  saveEscenasMentales: (items: EscenaMentalItem[]) =>
    replaceAll<{ success: boolean }>('escenasMentales', '/api/saveEscenasMentales', items, items.length),
};
